import { useErrorHandler } from '@shared/errors';
import { QueryClient, queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { Classification } from '@/features/classifications/types/classification';
import { meetingSchema } from '@/features/calendar/types/meeting';
import { artiaProtectedApi } from '@/lib/api-client';
import { MutationConfig, QueryConfig } from '@/lib/react-query';
import { notifySuccess } from '@/lib/notification/notifications';
import { buildQueryString } from '@/utils/filtering-functions';

export const queryKeys = {
  classificationsBase: ['classifications'],
  allClassifications: () => [...queryKeys.classificationsBase],
  classification: (id: number) => [...queryKeys.classificationsBase, id],
  classificationMeetings: (id: number, timeRange: string) => [...queryKeys.classificationsBase, id, 'meetings', timeRange],
};

export const resetClassificationsQuery = async (queryClient: QueryClient) => {
  await queryClient.invalidateQueries({ queryKey: queryKeys.classificationsBase });
};

// LIST
const getClassifications = async (): Promise<Classification[]> => {
  return await artiaProtectedApi.get('classifications');
};

const classificationsQueryOptions = () => {
  return queryOptions({
    queryKey: queryKeys.allClassifications(),
    queryFn: getClassifications,
  });
};

type UseClassificationsQueryOptions = {
  queryConfig?: QueryConfig<typeof classificationsQueryOptions>;
};

export const useClassificationsQuery = ({ queryConfig }: UseClassificationsQueryOptions = {}) => {
  return useQuery({
    ...classificationsQueryOptions(),
    ...queryConfig,
  });
};

// SHOW
const getClassification = async (id: number): Promise<Classification> => {
  return await artiaProtectedApi.get(`classifications/${id}`);
};

const classificationQueryOptions = (id: number) => {
  return queryOptions({
    queryKey: queryKeys.classification(id),
    queryFn: () => getClassification(id),
  });
};

type UseClassificationQueryOptions = {
  id: number;
  queryConfig?: QueryConfig<typeof classificationQueryOptions>;
};

export const useClassificationQuery = ({ id, queryConfig }: UseClassificationQueryOptions) => {
  return useQuery({
    ...classificationQueryOptions(id),
    ...queryConfig,
  });
};

// MEETINGS
const meetingsSchema = z.array(meetingSchema);

const getClassificationMeetings = async (id: number, timeRange: 'upcoming' | 'previous') => {
  const data = await artiaProtectedApi.get(`classifications/${id}/meetings${buildQueryString({ timeRange })}`);
  return meetingsSchema.parse(data);
};

const classificationMeetingsQueryOptions = (id: number, timeRange: 'upcoming' | 'previous') => {
  return queryOptions({
    queryKey: queryKeys.classificationMeetings(id, timeRange),
    queryFn: () => getClassificationMeetings(id, timeRange),
  });
};

type UseClassificationMeetingsOptions = {
  id: number;
  timeRange: 'upcoming' | 'previous';
  queryConfig?: QueryConfig<typeof classificationMeetingsQueryOptions>;
};

export const useClassificationMeetings = ({ id, timeRange, queryConfig }: UseClassificationMeetingsOptions) => {
  return useQuery({
    ...classificationMeetingsQueryOptions(id, timeRange),
    ...queryConfig,
  });
};

// UPDATE
const updateClassification = async ({ id, name }: { id: number; name: string }): Promise<void> => {
  return await artiaProtectedApi.put(`classifications/${id}`, { name });
};

type UseUpdateClassificationOptions = {
  mutationConfig?: MutationConfig<typeof updateClassification>;
};

export const useUpdateClassification = ({ mutationConfig }: UseUpdateClassificationOptions = {}) => {
  const queryClient = useQueryClient();
  const { handleError } = useErrorHandler();

  const { onError, onSuccess, ...restConfig } = mutationConfig || {};

  return useMutation({
    onSuccess: async (...args) => {
      notifySuccess({ title: 'Saved', message: 'Classification name updated.' });
      await resetClassificationsQuery(queryClient);
      onSuccess?.(...args);
    },
    onError: (...args) => {
      const [error] = args;
      handleError(error, { title: 'Save Failed', message: 'Unable to update classification.' });
      onError?.(...args);
    },
    mutationFn: updateClassification,
    ...restConfig,
  });
};

// ADD DRUG
const addDrugToClassification = async ({ classificationId, drugId }: { classificationId: number; drugId: number }): Promise<void> => {
  return await artiaProtectedApi.post(`classifications/${classificationId}/drugs`, { drugId });
};

type UseAddDrugToClassificationOptions = {
  mutationConfig?: MutationConfig<typeof addDrugToClassification>;
};

export const useAddDrugToClassification = ({ mutationConfig }: UseAddDrugToClassificationOptions = {}) => {
  const queryClient = useQueryClient();
  const { handleError } = useErrorHandler();

  const { onError, onSuccess, ...restConfig } = mutationConfig || {};

  return useMutation({
    onSuccess: async (...args) => {
      await resetClassificationsQuery(queryClient);
      onSuccess?.(...args);
    },
    onError: (...args) => {
      const [error] = args;
      handleError(error, { title: 'Failed', message: 'Unable to add drug to classification.' });
      onError?.(...args);
    },
    mutationFn: addDrugToClassification,
    ...restConfig,
  });
};

// REMOVE DRUG
const removeDrugFromClassification = async ({ classificationId, drugId }: { classificationId: number; drugId: number }): Promise<void> => {
  return await artiaProtectedApi.delete(`classifications/${classificationId}/drugs/${drugId}`);
};

type UseRemoveDrugFromClassificationOptions = {
  mutationConfig?: MutationConfig<typeof removeDrugFromClassification>;
};

export const useRemoveDrugFromClassification = ({ mutationConfig }: UseRemoveDrugFromClassificationOptions = {}) => {
  const queryClient = useQueryClient();
  const { handleError } = useErrorHandler();

  const { onError, onSuccess, ...restConfig } = mutationConfig || {};

  return useMutation({
    onSuccess: async (...args) => {
      await resetClassificationsQuery(queryClient);
      onSuccess?.(...args);
    },
    onError: (...args) => {
      const [error] = args;
      handleError(error, { title: 'Failed', message: 'Unable to remove drug from classification.' });
      onError?.(...args);
    },
    mutationFn: removeDrugFromClassification,
    ...restConfig,
  });
};

// DELETE
const deleteClassification = async (classification: Classification): Promise<void> => {
  return await artiaProtectedApi.delete(`classifications/${classification.id}`);
};

type UseDeleteClassificationOptions = {
  mutationConfig?: MutationConfig<typeof deleteClassification>;
};

export const useDeleteClassification = ({ mutationConfig }: UseDeleteClassificationOptions = {}) => {
  const queryClient = useQueryClient();
  const { handleError } = useErrorHandler();

  const { onError, onSuccess, ...restConfig } = mutationConfig || {};

  return useMutation({
    onSuccess: async (...args) => {
      notifySuccess({ title: 'Deleted', message: 'Classification was successfully deleted.' });
      await resetClassificationsQuery(queryClient);
      onSuccess?.(...args);
    },
    onError: (...args) => {
      const [error] = args;
      handleError(error, { title: 'Delete Failed', message: 'Unable to delete classification.' });
      onError?.(...args);
    },
    mutationFn: deleteClassification,
    ...restConfig,
  });
};
